import React, { useCallback, useMemo, useState } from 'react'
import RdnaSmartTable from '@ringdna/common/src/components/RdnaTable/RdnaSmartTable'
import SectionHeader from '@ringdna/common/src/components/RdnaHeaderSection'
import RdnaButton from '@ringdna/common/src/components/RdnaButton'
import ComplianceAnnouncementPanel from './ComplianceAnnouncementPanel'
import { Body } from '../../styles'
import { useComplianceAnnouncements } from '../../api'
import { ComplianceAnnouncement, MainProps } from '../../types'
import RdnaMenu from '@ringdna/common/src/components/RdnaMenu'
import ConfirmComplianceAnnouncementDeleteModal from './ComplianceAnnouncementDeleteModal'
import { NO_RESULT_STATE, DELETE, EDIT, initComplianceAnnouncements } from './const'
import { getColumns } from './columns'

export default function ComplianceAnnouncementsTable({
  accountId,
  isEditingAllowed
}: { isEditingAllowed: boolean } & MainProps) {
  const [payload, , loading, refetch] = useComplianceAnnouncements({ meta: { accountId: accountId } })
  const [complianceAnnouncements, setComplianceAnnouncements] =
    useState<ComplianceAnnouncement>(initComplianceAnnouncements)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const handleActionMenu = useCallback((action: string, complianceAnnouncement: ComplianceAnnouncement) => {
    setComplianceAnnouncements(complianceAnnouncement)
    if (action === DELETE) {
      setIsDeleteModalOpen(true)
    } else if (action === EDIT) {
      setIsModalOpen(true)
    }
  }, [])

  const memoPayload = useMemo(() => {
    return payload?.map((template: ComplianceAnnouncement) => ({
      ...template,
      isPlaying: false,
      menu: (
        <RdnaMenu
          options={[
            { text: EDIT, disabled: !isEditingAllowed },
            { text: DELETE, disabled: !isEditingAllowed }
          ]}
          onSelect={item => handleActionMenu(item, template)}
        />
      )
    }))
  }, [payload, isEditingAllowed, handleActionMenu])

  async function closeModals(isFetch: boolean) {
    setIsModalOpen(false)
    setIsDeleteModalOpen(false)
    setComplianceAnnouncements(initComplianceAnnouncements)
    if (isFetch) {
      refetch()
    }
  }

  return (
    <Body data-testid="compliance-announcements-container">
      <SectionHeader
        descriptionText={'Create preconfigured compliance announcements for users to play back during live calls.'}
        rightColumn={
          <RdnaButton
            style={{ width: 'fit-content' }}
            text={'Add Announcement'}
            name="add-announcement"
            disabled={!isEditingAllowed}
            onClick={() => {
              setIsModalOpen(!isModalOpen)
            }}
          />
        }
      />
      <RdnaSmartTable
        searchPlaceholder="Search"
        // @ts-ignore
        columns={getColumns()}
        data={memoPayload || []}
        noResultState={NO_RESULT_STATE}
        isFetching={!!loading}
        paginationSize={10}
      />
      <ComplianceAnnouncementPanel
        accountId={accountId}
        open={isModalOpen}
        onClose={closeModals}
        complianceAnnouncement={complianceAnnouncements}
      />
      <ConfirmComplianceAnnouncementDeleteModal
        open={isDeleteModalOpen}
        onClose={closeModals}
        id={complianceAnnouncements?.id}
        name={complianceAnnouncements?.name}
      />
    </Body>
  )
}
